import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Lightning, Eye, Calendar, Clock, CaretLeft, CaretRight,
  Trophy, Table as TableIcon, Video, YoutubeLogo
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toLocalDateString, todayLocalDateString } from "@/lib/localDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LiveMatchCard({ match }) {
  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

  return (
    <Card className="bg-card border-border border-l-4 border-l-red-500 overflow-hidden" data-testid={`live-match-${match.id}`}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <span className="relative flex h-2 w-2 mr-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            LIVE
          </Badge>
          <span className="text-xs text-muted-foreground truncate">{match.tournament_name}</span>
        </div>
        <div className="flex items-center gap-2">
          {match.table_name && <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">{match.table_name}</Badge>}
          <span className="text-[10px] text-muted-foreground capitalize">{match.round?.replace(/_/g, ' ')}</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between py-3">
          <div className="flex-1 text-center">
            <Avatar className="h-12 w-12 sm:h-14 sm:w-14 mx-auto border-2 border-border">
              <AvatarImage src={match.player1_photo} />
              <AvatarFallback className="text-sm">{getInitials(match.player1_name)}</AvatarFallback>
            </Avatar>
            <p className="mt-1.5 text-sm font-medium truncate">{match.player1_name}</p>
          </div>
          <div className="px-4">
            <div className="flex items-center gap-3">
              <span className="text-3xl sm:text-4xl font-heading font-bold text-primary">{match.player1_score}</span>
              <span className="text-xl text-muted-foreground">-</span>
              <span className="text-3xl sm:text-4xl font-heading font-bold text-primary">{match.player2_score}</span>
            </div>
            <p className="text-center text-[10px] text-muted-foreground mt-1">Bo{match.best_of || 5}</p>
          </div>
          <div className="flex-1 text-center">
            <Avatar className="h-12 w-12 sm:h-14 sm:w-14 mx-auto border-2 border-border">
              <AvatarImage src={match.player2_photo} />
              <AvatarFallback className="text-sm">{getInitials(match.player2_name)}</AvatarFallback>
            </Avatar>
            <p className="mt-1.5 text-sm font-medium truncate">{match.player2_name}</p>
          </div>
        </div>

        {/* Current frame */}
        {match.frames && match.frames.length > 0 && (() => {
          const currentFrame = match.frames.find(f => !f.is_complete) || match.frames[match.frames.length - 1];
          return (
            <div className="p-2.5 rounded-lg bg-muted/30 text-center">
              <p className="text-[10px] text-muted-foreground mb-1">Frame {currentFrame.frame_number}</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-xl font-heading font-bold">{currentFrame.player1_points}</span>
                <span className="text-muted-foreground text-xs">-</span>
                <span className="text-xl font-heading font-bold">{currentFrame.player2_points}</span>
              </div>
            </div>
          );
        })()}

        <div className="mt-3 flex gap-2">
          <Link to={`/live/${match.id}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full border-border text-sm">
              <Eye className="w-4 h-4 mr-1.5" />Watch Live
            </Button>
          </Link>
          {match.youtube_url && (
            <a href={match.youtube_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-destructive/30 text-destructive">
                <YoutubeLogo className="w-4 h-4" weight="fill" />
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ScheduledMatchRow({ match }) {
  const isCompleted = match.status === "completed" || match.status === "walkover";
  const p1Won = isCompleted && match.winner_id === match.player1_id;
  const p2Won = isCompleted && match.winner_id === match.player2_id;

  return (
    <div
      className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border transition-colors ${
        isCompleted ? 'border-primary/20 bg-primary/5' : 'border-border bg-card hover:border-muted-foreground/30'
      }`}
      data-testid={`matchday-match-${match.id}`}
    >
      {/* Time */}
      <div className="flex flex-col items-center w-14 sm:w-16 flex-shrink-0 text-center">
        <span className="text-xs sm:text-sm font-bold text-foreground">{match.scheduled_time || "TBD"}</span>
        {match.table_name && (
          <span className="text-[9px] sm:text-[10px] text-muted-foreground">{match.table_name}</span>
        )}
      </div>

      {/* Divider */}
      <div className="w-px h-8 bg-border flex-shrink-0" />

      {/* Players & Score */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span className={`text-xs sm:text-sm truncate flex-1 ${p1Won ? 'font-bold text-primary' : 'font-medium'}`}>
            {match.player1_name || "TBD"}
          </span>
          <span className={`text-xs sm:text-sm font-heading font-bold w-4 text-center ${p1Won ? 'text-primary' : ''}`}>
            {isCompleted ? match.player1_score : ""}
          </span>
        </div>
        <div className="flex items-center justify-between gap-1">
          <span className={`text-xs sm:text-sm truncate flex-1 ${p2Won ? 'font-bold text-primary' : 'font-medium'}`}>
            {match.player2_name || "TBD"}
          </span>
          <span className={`text-xs sm:text-sm font-heading font-bold w-4 text-center ${p2Won ? 'text-primary' : ''}`}>
            {isCompleted ? match.player2_score : ""}
          </span>
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
        {match.round && (
          <Badge variant="outline" className="text-[9px] sm:text-[10px] capitalize border-border">
            {match.round.replace(/_/g, ' ')}
          </Badge>
        )}
        {isCompleted && (
          <span className="text-[9px] text-primary font-medium">FT</span>
        )}
      </div>
    </div>
  );
}

export default function MatchDayLive() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(todayLocalDateString());
  const [dayMatches, setDayMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  // Map of YYYY-MM-DD → count of matches on that day (scheduled + live). Used
  // to show a match-count badge on each day pill in the date strip.
  const [monthCounts, setMonthCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (date) => {
    try {
      const [dayRes, liveRes] = await Promise.all([
        axios.get(`${API}/matches/by-date/${date}`).catch(() => ({ data: [] })),
        axios.get(`${API}/matches/live`).catch(() => ({ data: [] })),
      ]);
      setDayMatches(dayRes.data || []);
      setLiveMatches(liveRes.data || []);
    } catch {
      setDayMatches([]);
      setLiveMatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load match counts for the current month + the adjacent months so the
  // 7-day strip always has accurate badges even when it spills across months.
  const fetchMonthCounts = useCallback(async (anchorDate) => {
    const anchor = new Date(anchorDate + "T00:00:00");
    const months = new Set();
    for (const offset of [-1, 0, 1]) {
      const d = new Date(anchor);
      d.setDate(1);
      d.setMonth(d.getMonth() + offset);
      months.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    try {
      const results = await Promise.all(
        Array.from(months).map(m =>
          axios.get(`${API}/matches/calendar`, { params: { month: m } }).catch(() => ({ data: [] }))
        )
      );
      const counts = {};
      results.forEach(r => {
        (r.data || []).forEach(m => {
          if (m.scheduled_date) counts[m.scheduled_date] = (counts[m.scheduled_date] || 0) + 1;
        });
      });
      setMonthCounts(counts);
    } catch (err) {
      console.error("Failed to load month counts:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedDate);
    fetchMonthCounts(selectedDate);
    // Auto-refresh live matches every 5 seconds
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/matches/live`);
        setLiveMatches(res.data || []);
      } catch (err) { console.error("Live match refresh failed:", err.message); }
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedDate, fetchData, fetchMonthCounts]);

  const changeDate = (days) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + days);
    setSelectedDate(toLocalDateString(d));
  };

  const goToday = () => setSelectedDate(todayLocalDateString());
  const isToday = selectedDate === todayLocalDateString();

  // Seven-day strip centred on the selected date: -3 → +3 relative
  const dateStrip = useMemo(() => {
    const anchor = new Date(selectedDate + "T00:00:00");
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(anchor);
      d.setDate(d.getDate() + (i - 3));
      const iso = toLocalDateString(d);
      return {
        iso,
        dayName: d.toLocaleDateString('en-GB', { weekday: 'short' }),
        dayNum: d.getDate(),
        monthShort: d.toLocaleDateString('en-GB', { month: 'short' }),
        isSelected: iso === selectedDate,
        isToday: iso === todayLocalDateString(),
        count: monthCounts[iso] || 0,
      };
    });
  }, [selectedDate, monthCounts]);

  const formatDateDisplay = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Separate day matches by status (exclude live — those show in the live section)
  const scheduledToday = dayMatches.filter(m => m.status === "scheduled");
  const completedToday = dayMatches.filter(m => m.status === "completed" || m.status === "walkover");

  // Group by table
  const groupByTable = (matches) => {
    const groups = {};
    const noTable = [];
    matches.forEach(m => {
      if (m.table_name) {
        if (!groups[m.table_name]) groups[m.table_name] = [];
        groups[m.table_name].push(m);
      } else {
        noTable.push(m);
      }
    });
    Object.values(groups).forEach(g => g.sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")));
    noTable.sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
    return { tableGroups: groups, noTable };
  };

  const { tableGroups, noTable } = groupByTable(scheduledToday);
  const tableNames = Object.keys(tableGroups).sort();
  const totalToday = dayMatches.length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3" data-testid="live-page-title">
              <Lightning className="w-6 h-6 sm:w-8 sm:h-8 text-secondary" weight="fill" />
              Match Centre
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Live scores and daily schedule</p>
          </div>
          {liveMatches.length > 0 && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <span className="text-sm text-muted-foreground">{liveMatches.length} Live</span>
            </div>
          )}
        </div>

        {/* Date Nav — arrow buttons + native date picker + horizontally scrollable 7-day strip.
            Closes the "can't scroll to check match schedules" gap by giving users three ways
            to pick a date: ◀/▶ day-step, direct date input (jump to any day), or tap a day pill. */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changeDate(-1)} className="border-border h-9 w-9 p-0 flex-shrink-0" data-testid="prev-day-btn">
              <CaretLeft className="w-4 h-4" weight="bold" />
            </Button>
            <div className="flex-1 flex items-center justify-center gap-2">
              <p className="text-sm sm:text-base font-semibold truncate" data-testid="matchday-date">{formatDateDisplay(selectedDate)}</p>
            </div>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => { if (e.target.value) setSelectedDate(e.target.value); }}
              className="w-[150px] h-9 text-xs bg-card border-border flex-shrink-0"
              data-testid="matchday-date-input"
              aria-label="Pick a date"
            />
            <Button variant="outline" size="sm" onClick={() => changeDate(1)} className="border-border h-9 w-9 p-0 flex-shrink-0" data-testid="next-day-btn">
              <CaretRight className="w-4 h-4" weight="bold" />
            </Button>
            {!isToday && (
              <Button variant="outline" size="sm" onClick={goToday} className="border-border text-xs h-9 flex-shrink-0" data-testid="today-btn">Today</Button>
            )}
          </div>

          {/* 7-day pill strip — horizontally scrollable on narrow viewports. */}
          <div
            className="flex items-stretch gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory"
            data-testid="matchday-date-strip"
          >
            {dateStrip.map((d) => (
              <button
                key={d.iso}
                type="button"
                onClick={() => setSelectedDate(d.iso)}
                className={`snap-start flex-shrink-0 relative flex flex-col items-center justify-center min-w-[56px] px-2 py-1.5 rounded-lg border transition-all ${
                  d.isSelected
                    ? "border-primary bg-primary/15 shadow-sm"
                    : d.isToday
                    ? "border-secondary/60 bg-secondary/5 hover:bg-secondary/10"
                    : "border-border hover:border-primary/40 hover:bg-muted/30"
                }`}
                data-testid={`date-pill-${d.iso}`}
                aria-pressed={d.isSelected}
              >
                <span className={`text-[10px] uppercase tracking-wider ${d.isSelected ? "text-primary" : "text-muted-foreground"}`}>
                  {d.dayName}
                </span>
                <span className={`font-heading font-bold text-lg leading-none tabular-nums ${d.isSelected ? "text-primary" : ""}`}>
                  {d.dayNum}
                </span>
                <span className="text-[9px] text-muted-foreground">{d.monthShort}</span>
                {d.count > 0 && (
                  <span
                    className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      d.isSelected ? "bg-primary text-primary-foreground" : "bg-secondary/80 text-secondary-foreground"
                    }`}
                    data-testid={`date-pill-count-${d.iso}`}
                  >
                    {d.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[30vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {/* LIVE MATCHES */}
          {liveMatches.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Lightning className="w-4 h-4 text-red-500" weight="fill" />
                <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider">Live Now</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {liveMatches.map(match => <LiveMatchCard key={match.id} match={match} />)}
              </div>
            </div>
          )}

          {/* SCHEDULED TODAY — grouped by table */}
          {scheduledToday.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Upcoming Today ({scheduledToday.length})
                </h2>
              </div>

              {tableNames.length > 0 ? (
                <div className={`grid gap-4 ${tableNames.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                  {tableNames.map(tableName => (
                    <div key={tableName}>
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <TableIcon className="w-3.5 h-3.5 text-primary" weight="bold" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-primary">{tableName}</span>
                        <span className="text-[10px] text-muted-foreground">({tableGroups[tableName].length})</span>
                      </div>
                      <div className="space-y-1.5">
                        {tableGroups[tableName].map(m => <ScheduledMatchRow key={m.id} match={m} />)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {noTable.length > 0 && (
                <div className={tableNames.length > 0 ? "mt-4" : ""}>
                  {tableNames.length > 0 && (
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Unassigned</span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {noTable.map(m => <ScheduledMatchRow key={m.id} match={m} />)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* COMPLETED TODAY */}
          {completedToday.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-primary" weight="fill" />
                <h2 className="text-sm font-semibold text-primary uppercase tracking-wider">
                  Results ({completedToday.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {completedToday.map(m => <ScheduledMatchRow key={m.id} match={m} />)}
              </div>
            </div>
          )}

          {/* EMPTY STATE */}
          {liveMatches.length === 0 && totalToday === 0 && (
            <Card className="bg-card border-border">
              <CardContent className="py-14 text-center">
                <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No matches {isToday ? 'today' : 'on this day'}</p>
                <p className="text-xs text-muted-foreground mt-1">Use the arrows to browse other dates</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </motion.div>
  );
}
